/**
 * Telegram Mini App SDK helpers.
 * Provides safe access to the TWA SDK (works in both browser and Telegram).
 */

interface SafeAreaInset {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

export interface TelegramWebApp {
  ready(): void;
  expand(): void;
  requestFullscreen?(): void | Promise<void>;
  exitFullscreen?(): void | Promise<void>;
  close(): void;
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      photo_url?: string;
      language_code?: string;
    };
    start_param?: string;
  };
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  version?: string;
  isExpanded: boolean;
  isFullscreen?: boolean;
  safeAreaInset?: SafeAreaInset;
  contentSafeAreaInset?: SafeAreaInset;
  viewportHeight: number;
  viewportStableHeight: number;
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    setText(text: string): void;
    setParams(params: Record<string, unknown>): void;
  };
  BackButton: {
    isVisible: boolean;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  HapticFeedback?: {
    impactOccurred?(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred?(type: 'error' | 'success' | 'warning'): void;
    selectionChanged?(): void;
  };
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
  onEvent(eventType: string, cb: (event?: unknown) => void): void;
  offEvent(eventType: string, cb: (event?: unknown) => void): void;
  sendData(data: string): void;
  openLink(url: string): void;
  openInvoice?(url: string, cb?: (status: string) => void): void;
  openTelegramLink(url: string): void;
  showPopup(params: unknown, cb?: (button_id: string) => void): void;
  showAlert(message: string, cb?: () => void): void;
  showConfirm(message: string, cb?: (confirmed: boolean) => void): void;
}

export interface WebUserProfile {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  language_code?: string;
}

const WEB_USER_STORAGE_KEY = 'elmental.webUser';
const LEGACY_WEB_USER_STORAGE_KEY = 'elmental.devUser';
const TELEGRAM_USER_STORAGE_KEY = 'elmental.telegramUser';
const TELEGRAM_FULLSCREEN_TOP_RESERVE = 88;
let telegramFullscreenRequested = false;

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

/**
 * Returns the Telegram WebApp object, or null when running outside Telegram.
 */
export function getTelegramWebApp(): TelegramWebApp | null {
  const app = getTelegramRuntime();
  if (!app) return null;
  if (!app.initData && !app.initDataUnsafe?.user && !getTelegramInitDataFromLocation()) return null;
  return app;
}

function getTelegramRuntime(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/**
 * Initialize the TWA: call ready() and expand().
 */
export function initTelegram(): void {
  const twa = getTelegramRuntime();
  if (!twa) return;
  try {
    twa.ready();
    twa.expand();
    telegramFullscreenRequested = twa.isFullscreen === true;
    if (telegramVersionAtLeast(twa.version, '8.0') && typeof twa.requestFullscreen === 'function' && twa.isFullscreen !== true) {
      telegramFullscreenRequested = true;
      applyFullscreenTopReserve();
      try {
        void Promise.resolve(twa.requestFullscreen()).catch(() => {
          telegramFullscreenRequested = false;
          // Fullscreen is not supported on every Telegram surface.
        });
      } catch {
        telegramFullscreenRequested = false;
        // Fullscreen is not supported on every Telegram surface.
      }
    }
    if (telegramVersionAtLeast(twa.version, '6.1')) {
      twa.setHeaderColor?.('#111a2f');
      twa.setBackgroundColor?.('#111a2f');
    }
    if (telegramVersionAtLeast(twa.version, '7.10')) {
      twa.setBottomBarColor?.('#111a2f');
    }
  } catch {
    // Ignore Telegram chrome failures. They are not critical.
  }
}

function applyFullscreenTopReserve(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--elmental-js-safe-top', `${TELEGRAM_FULLSCREEN_TOP_RESERVE}px`);
}

function telegramVersionAtLeast(version: string | undefined, minimum: string): boolean {
  if (!version) return true;
  const currentParts = version.split('.').map((part) => Number.parseInt(part, 10));
  const minimumParts = minimum.split('.').map((part) => Number.parseInt(part, 10));
  const length = Math.max(currentParts.length, minimumParts.length);
  for (let index = 0; index < length; index += 1) {
    const current = Number.isFinite(currentParts[index]) ? currentParts[index] : 0;
    const required = Number.isFinite(minimumParts[index]) ? minimumParts[index] : 0;
    if (current > required) return true;
    if (current < required) return false;
  }
  return true;
}

export function installTelegramViewportSync(): () => void {
  const twa = getTelegramRuntime();
  if (!twa || typeof document === 'undefined') return () => {};

  const applyViewport = () => {
    const root = document.documentElement;
    const viewportHeight = Math.round(twa.viewportStableHeight || twa.viewportHeight || window.innerHeight || 0);
    if (viewportHeight > 0) {
      root.style.setProperty('--elmental-js-viewport-height', `${viewportHeight}px`);
    }

    const safe = twa.safeAreaInset ?? {};
    const contentSafe = twa.contentSafeAreaInset ?? {};
    const fullscreenChromeSafeTop = twa.isFullscreen === true || telegramFullscreenRequested
      ? TELEGRAM_FULLSCREEN_TOP_RESERVE
      : 0;
    root.style.setProperty(
      '--elmental-js-safe-top',
      `${Math.max(toInset(safe.top), toInset(contentSafe.top), fullscreenChromeSafeTop)}px`,
    );
    root.style.setProperty('--elmental-js-safe-right', `${Math.max(toInset(safe.right), toInset(contentSafe.right))}px`);
    root.style.setProperty('--elmental-js-safe-bottom', `${Math.max(toInset(safe.bottom), toInset(contentSafe.bottom))}px`);
    root.style.setProperty('--elmental-js-safe-left', `${Math.max(toInset(safe.left), toInset(contentSafe.left))}px`);
  };

  const applyFullscreenFailure = () => {
    telegramFullscreenRequested = false;
    applyViewport();
  };

  const events = ['viewportChanged', 'safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged'];
  for (const event of events) twa.onEvent(event, applyViewport);
  twa.onEvent('fullscreenFailed', applyFullscreenFailure);

  applyViewport();
  const timers = [window.setTimeout(applyViewport, 100), window.setTimeout(applyViewport, 600)];

  return () => {
    for (const event of events) twa.offEvent(event, applyViewport);
    twa.offEvent('fullscreenFailed', applyFullscreenFailure);
    for (const timer of timers) window.clearTimeout(timer);
  };
}

function toInset(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

/**
 * Get the Telegram user from initDataUnsafe.
 * Returns null when running outside Telegram.
 */
export function getTelegramUser() {
  const hashInitData = getTelegramInitDataFromLocation();
  const twa = getTelegramWebApp();
  if (!twa) return parseTelegramUserFromInitData(hashInitData);
  return twa.initDataUnsafe?.user ?? parseTelegramUserFromInitData(twa.initData || hashInitData);
}

/**
 * Raw signed Telegram initData used by the backend auth endpoint.
 */
export function getTelegramInitData(): string {
  const hashInitData = getTelegramInitDataFromLocation();
  const twa = getTelegramWebApp();
  if (!twa) return hashInitData;
  return twa.initData || hashInitData;
}

export function cacheTelegramUser(user: WebUserProfile): void {
  const cached = normalizeTelegramUser(user);
  if (!cached) return;
  writeStorage(TELEGRAM_USER_STORAGE_KEY, JSON.stringify({ ...cached, cachedAt: Date.now() }));
}

export function getCachedTelegramUser(): WebUserProfile | null {
  const raw = readStorage(TELEGRAM_USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<WebUserProfile>;
    return normalizeTelegramUser(parsed);
  } catch {
    removeStorage(TELEGRAM_USER_STORAGE_KEY);
    return null;
  }
}

function getTelegramInitDataFromLocation(): string {
  if (typeof window === 'undefined') return '';
  const candidates = [
    window.location.hash?.replace(/^#/, '') ?? '',
    window.location.search?.replace(/^\?/, '') ?? '',
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const params = new URLSearchParams(candidate);
    const initData = params.get('tgWebAppData');
    if (initData) return initData;
  }

  return '';
}

function parseTelegramUserFromInitData(initData: string): WebUserProfile | null {
  if (!initData) return null;
  try {
    const rawUser = new URLSearchParams(initData).get('user');
    if (!rawUser) return null;
    const user = JSON.parse(rawUser) as Partial<WebUserProfile>;
    if (typeof user.id !== 'number' || typeof user.first_name !== 'string') return null;
    return {
      id: user.id,
      first_name: user.first_name,
      last_name: typeof user.last_name === 'string' ? user.last_name : undefined,
      username: typeof user.username === 'string' ? user.username : undefined,
      photo_url: typeof user.photo_url === 'string' ? user.photo_url : undefined,
      language_code: typeof user.language_code === 'string' ? user.language_code : undefined,
    };
  } catch {
    return null;
  }
}

function normalizeTelegramUser(user: Partial<WebUserProfile>): WebUserProfile | null {
  if (typeof user.id !== 'number' || !Number.isFinite(user.id)) return null;
  if (typeof user.first_name !== 'string' || !user.first_name.trim()) return null;
  return {
    id: user.id,
    first_name: user.first_name,
    last_name: typeof user.last_name === 'string' ? user.last_name : undefined,
    username: typeof user.username === 'string' ? user.username : undefined,
    photo_url: typeof user.photo_url === 'string' ? user.photo_url : undefined,
    language_code: typeof user.language_code === 'string' ? user.language_code : undefined,
  };
}

/**
 * Haptic feedback helpers (no-op outside Telegram).
 */
export const haptic = {
  light: () => safeHapticImpact('light'),
  medium: () => safeHapticImpact('medium'),
  heavy: () => safeHapticImpact('heavy'),
  success: () => safeHapticNotification('success'),
  error: () => safeHapticNotification('error'),
  warning: () => safeHapticNotification('warning'),
  selection: () => safeHapticSelection(),
};

function safeHapticImpact(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void {
  try {
    getTelegramWebApp()?.HapticFeedback?.impactOccurred?.(style);
  } catch {
    // Haptics are optional in Telegram clients and must never block gameplay.
  }
}

function safeHapticNotification(type: 'error' | 'success' | 'warning'): void {
  try {
    getTelegramWebApp()?.HapticFeedback?.notificationOccurred?.(type);
  } catch {
    // Haptics are optional in Telegram clients and must never block gameplay.
  }
}

function safeHapticSelection(): void {
  try {
    getTelegramWebApp()?.HapticFeedback?.selectionChanged?.();
  } catch {
    // Haptics are optional in Telegram clients and must never block gameplay.
  }
}

/**
 * Show a native Telegram alert.
 */
export function showAlert(message: string): Promise<void> {
  return new Promise((resolve) => {
    const twa = getTelegramWebApp();
    if (!twa) {
      alert(message);
      resolve();
      return;
    }
    twa.showAlert(message, resolve);
  });
}

/**
 * Show a native Telegram confirmation dialog.
 */
export function showConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const twa = getTelegramWebApp();
    if (!twa) {
      resolve(confirm(message));
      return;
    }
    twa.showConfirm(message, resolve);
  });
}

/**
 * Generate a mock user for development/testing outside Telegram.
 */
export function getMockUser() {
  const params = new URLSearchParams(window.location.search);
  const explicitName = params.get('player') ?? params.get('user');
  if (explicitName) {
    const cleanName = sanitizeWebUserName(explicitName) || 'Player';
    let hash = 0;
    for (let i = 0; i < cleanName.length; i++) {
      hash = (hash * 31 + cleanName.charCodeAt(i)) >>> 0;
    }
    return {
      id: 100_000_000 + (hash % 800_000_000),
      first_name: cleanName,
      last_name: undefined,
      username: userNameFromDisplayName(cleanName),
      photo_url: undefined,
    };
  }

  const stored = readStorage(WEB_USER_STORAGE_KEY) ?? readStorage(LEGACY_WEB_USER_STORAGE_KEY);
  if (stored) {
    try {
      const user = JSON.parse(stored) as WebUserProfile;
      if (typeof user.id === 'number' && typeof user.first_name === 'string') {
        return saveWebUser(user);
      }
    } catch {
      removeStorage(WEB_USER_STORAGE_KEY);
      removeStorage(LEGACY_WEB_USER_STORAGE_KEY);
    }
  }

  const id = Math.floor(100_000_000 + Math.random() * 800_000_000);
  const user = {
    id,
    first_name: `Player${String(id).slice(-4)}`,
    last_name: undefined,
    username: `player_${String(id).slice(-6)}`,
    photo_url: undefined,
  };
  return saveWebUser(user);
}

export function saveWebUser(user: WebUserProfile): WebUserProfile {
  const firstName = sanitizeWebUserName(user.first_name) || `Player${String(user.id).slice(-4)}`;
  const saved = {
    id: user.id,
    first_name: firstName,
    last_name: undefined,
    username: userNameFromDisplayName(firstName),
    photo_url: user.photo_url,
  };
  const payload = JSON.stringify(saved);
  writeStorage(WEB_USER_STORAGE_KEY, payload);
  writeStorage(LEGACY_WEB_USER_STORAGE_KEY, payload);
  return saved;
}

export function sanitizeWebUserName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').slice(0, 32);
}

export function userNameFromDisplayName(name: string): string {
  const normalized = sanitizeWebUserName(name)
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
  return normalized || 'player';
}

function readStorage(key: string): string | null {
  try {
    const localValue = window.localStorage.getItem(key);
    if (localValue) return localValue;
  } catch {
    // Ignore storage restrictions.
  }
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage restrictions.
  }
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage restrictions.
  }
}

function removeStorage(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage restrictions.
  }
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Ignore storage restrictions.
  }
}
